import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { Store, createSelector } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { SharedModels } from '@share-models';
import { PreloaderColor } from '@enums/shared.enum';
import { features } from '@consts/text.const';
import { routerSegments } from '@consts/routes';
import { AppState } from '../../../../../../store/app.state';
import { PersonGroupsActions } from '@states/person-groups/person-groups.actions';
import { personGroupsFeature } from '@states/person-groups/person-groups.feature';
import { PersonGroupsModel } from '@models/person-groups.model';
import { syncQueryParam } from '../../../../../../helpers/sync-query-param.helper';
import {
  CellTemplateContext,
  DatasetTableConfig,
  UiDatasetTableComponent,
} from '@ui/lib/ui-kit/ui-dataset-table';
import { UiButtonComponent, UiNoDataComponent, RouteModalService } from '@ui';
import { UiPreloaderComponent } from '../../../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiSearchComponent } from '../../../../../../shared/ui-kit/ui-search/ui-search.component';
import { createPersonGroupsTableConfig } from './person-groups-table.util';
import { PersonGroupsDialogService } from '../../../../../../services/person-groups-dialog.service';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';

@Component({
  selector: 'app-person-groups',
  imports: [
    TranslatePipe,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiSearchComponent,
    UiButtonComponent,
    UiDatasetTableComponent,
  ],
  templateUrl: './person-groups.component.html',
  styleUrl: './person-groups.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonGroupsComponent implements OnInit {
  private readonly store$ = inject(Store<AppState>);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly routeModalService = inject(RouteModalService);
  private readonly personGroupsDialogService = inject(PersonGroupsDialogService);

  protected readonly sharedCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<PersonGroupsModel.PersonGroupListDocument>>>('sharedCell');

  public readonly loaderColor = PreloaderColor;
  public readonly initialEmpty = features.personGroups.initialEmpty;
  public readonly noResults = features.personGroups.noResults;
  public readonly viewStates = SharedModels.ViewState;

  protected readonly search = syncQueryParam<string>('query');

  public readonly selectIsLoading = this.store$.selectSignal(
    createSelector(personGroupsFeature.selectPersonGroups, (state) => state.isLoading),
  );
  protected readonly selectIsJoiningPersonGroup = this.store$.selectSignal(personGroupsFeature.selectIsJoiningPersonGroup);
  protected readonly selectIsCreatingPersonGroup = this.store$.selectSignal(personGroupsFeature.selectIsCreatingPersonGroup);

  public readonly selectPersonGroups: Signal<PersonGroupsModel.PersonGroupListDocument[]> =
    this.store$.selectSignal(personGroupsFeature.selectAllPersonGroups);
  public readonly selectProcessingIds: Signal<string[]> =
    this.store$.selectSignal(personGroupsFeature.selectPersonGroupProcessingIds);

  public readonly filteredGroups = computed(() => {
    const raw = this.search();
    const q = String(raw ?? '').trim().toLowerCase();
    const list = this.selectPersonGroups();
    if (!q) {
      return list;
    }
    return list.filter((g) => g.name.toLowerCase().includes(q));
  });

  public readonly tableConfig =
    computed<DatasetTableConfig<PersonGroupsModel.PersonGroupListDocument>>(() => {
      const sharedTpl = this.sharedCellTemplate();
      return createPersonGroupsTableConfig({
        processingIds: () => this.selectProcessingIds(),
        templates: sharedTpl ? { sharedCell: sharedTpl } : undefined,
        onRowClick: (row) => {
          void this.router.navigate([
            routerSegments.database,
            routerSegments.personGroups,
            row._id,
          ]);
        },
      });
    });

  public get hasActiveFilters(): boolean {
    return String(this.search() ?? '').trim().length > 0;
  }

  public readonly viewState = computed(() => {
    if (this.filteredGroups().length > 0) {
      return SharedModels.ViewState.HAS_CONTENT;
    }
    return this.hasActiveFilters
      ? SharedModels.ViewState.NO_RESULTS
      : SharedModels.ViewState.INITIAL_EMPTY;
  });

  public ngOnInit(): void {
    this.store$.dispatch(PersonGroupsActions.startLoadingPersonGroups());

    this.routeModalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.joinPersonGroup,
          open: (params) => this.personGroupsDialogService.openJoinDialog(params),
          onClose: () => {
            this.routeModalService.removeQueryParam([
              openDialogByParams.joinPersonGroup,
              PersonGroupsModel.JoinRouteQueryParam.InviteCode,
            ]);
          },
        },
      ],
      this.destroyRef,
    );
  }

  public join(): void {
    this.routeModalService.openModal(openDialogByParams.joinPersonGroup);
  }

  public create(): void {
    this.personGroupsDialogService.openCreate();
  }

  public onRowAction(event: PersonGroupsModel.DatasetRowActionEvent): void {
    switch (event.actionId) {
      case PersonGroupsModel.RowActionId.Share:
        this.personGroupsDialogService.openShare(event.row._id, event.row.name, event.row.createdByOrgId);
        break;
      case PersonGroupsModel.RowActionId.Edit:
        this.personGroupsDialogService.openEdit(event.row._id, event.row.name);
        break;
      case PersonGroupsModel.RowActionId.Delete:
        this.store$.dispatch(PersonGroupsActions.requestDeletePersonGroup({ id: event.row._id, name: event.row.name }));
        break;
      default:
        console.log('PersonGroupsComponent.onRowAction', event.actionId, event.row._id);
    }
  }
}
